import { isLocale, LOCALE_META, type Locale, matchLocale } from '@refego/core/locales'
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { type Catalog, type Dict, en } from '../locales/en'

export type { Locale }

type Key = keyof Dict

// English ships in the main bundle; every other catalog is its own chunk, loaded on demand.
const loaders = import.meta.glob<{ default: Catalog }>(['../locales/*.ts', '!../locales/en.ts'])
const catalogs: Partial<Record<Locale, Catalog>> = { en }

export async function loadCatalog(l: Locale): Promise<Catalog> {
  const loaded = catalogs[l] ?? (await loaders[`../locales/${l}.ts`]?.())?.default ?? {}
  catalogs[l] = loaded
  return loaded
}

const translated = (l: Locale) => l === 'en' || Object.keys(catalogs[l] ?? {}).length > 0

/**
 * Saved choice first, then the browser languages. An auto-detected locale without a translation yet
 * stays English; one picked by hand is kept (dates and numbers still follow it).
 */
export async function initialLocale(): Promise<Locale> {
  try {
    const saved = localStorage.getItem('rg_locale')
    if (isLocale(saved)) {
      await loadCatalog(saved)
      return saved
    }
  } catch {}
  const detected = matchLocale(navigator.languages?.length ? navigator.languages : [navigator.language])
  await loadCatalog(detected)
  return translated(detected) ? detected : 'en'
}

interface I18n {
  locale: Locale
  t: (k: Key, vars?: Record<string, string | number>) => string
  has: (k: string) => k is Key
  setLocale: (l: Locale) => void
}

const Ctx = createContext<I18n | null>(null)

export function I18nProvider({ locale: first, children }: { locale: Locale; children: ReactNode }) {
  const [locale, set] = useState<Locale>(first)

  // lang and dir describe the text on screen, so an untranslated locale keeps English, left to right
  useEffect(() => {
    const shown = translated(locale) ? locale : 'en'
    document.documentElement.lang = shown
    document.documentElement.dir = LOCALE_META[shown].rtl ? 'rtl' : 'ltr'
  }, [locale])

  const value = useMemo<I18n>(() => {
    const dict = catalogs[locale] ?? {}
    return {
      locale,
      t: (k, vars) => {
        let s = dict[k] ?? en[k] ?? k
        if (vars) for (const [n, v] of Object.entries(vars)) s = s.replaceAll(`{${n}}`, String(v))
        return s
      },
      has: (k): k is Key => k in en,
      setLocale: (l) => {
        loadCatalog(l).then(
          () => set(l),
          () => set(l), // a failed chunk load still switches; texts fall back to English
        )
        try {
          localStorage.setItem('rg_locale', l)
        } catch {}
        fetch('/api/me', {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ locale: l }),
        }).catch(() => {})
      },
    }
  }, [locale])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useT() {
  const c = useContext(Ctx)
  if (!c) throw new Error('I18nProvider missing')
  return c
}
